import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import type { MessagePage } from "@bw/shared/types/generated";

import { queryKeys } from "../../../common/query/queryClient";
import {
  combineReadAuthority,
  exactReadAuthority,
  retainStillEligibleAuthority,
  type MessageHistoryData,
} from "./messageReadAuthority";
import {
  confirmedTombstones,
  mergeHeadAuthority,
  preserveTombstonesInPage,
  rebuildMessageHistory,
} from "./messageCacheAuthority";

const MAX_READ_AUTHORITY_CURSORS = 256;

function batchSignature(messageIds: string[]): string {
  return JSON.stringify(messageIds);
}

type ReadAuthorityCursors = Record<string, string | null>;

/** Retain a bounded cursor lookup for exact server-issued read batches. */
export function recordReadAuthorityCursor(
  queryClient: QueryClient,
  conversationId: string,
  messageIds: string[],
  cursor: string | null,
): void {
  if (messageIds.length === 0) return;
  queryClient.setQueryData<ReadAuthorityCursors>(
    queryKeys.social.messageReadAuthority(conversationId),
    (current) => {
      const entries = Object.entries({
        ...(current ?? {}),
        [batchSignature(messageIds)]: cursor,
      });
      return Object.fromEntries(entries.slice(-MAX_READ_AUTHORITY_CURSORS));
    },
  );
}

export function readAuthorityCursor(
  queryClient: QueryClient,
  conversationId: string,
  messageIds: string[],
): string | null | undefined {
  return queryClient.getQueryData<ReadAuthorityCursors>(
    queryKeys.social.messageReadAuthority(conversationId),
  )?.[batchSignature(messageIds)];
}

export function replaceReadAuthorityCursor(
  queryClient: QueryClient,
  conversationId: string,
  staleMessageIds: string[],
  freshMessageIds: string[],
  cursor: string | null,
): void {
  queryClient.setQueryData<ReadAuthorityCursors>(
    queryKeys.social.messageReadAuthority(conversationId),
    (current) => {
      const next = { ...(current ?? {}) };
      delete next[batchSignature(staleMessageIds)];
      if (freshMessageIds.length > 0) {
        next[batchSignature(freshMessageIds)] = cursor;
      }
      return next;
    },
  );
}

export function messageProjectionSignature(
  data: InfiniteData<MessagePage> | undefined,
  messageIds: string[],
): string {
  const selected = new Set(messageIds);
  return JSON.stringify(
    data?.pages.flatMap((page) =>
      page.items.filter((message) => selected.has(message.message_id)),
    ) ?? [],
  );
}

/**
 * Replace one stale server page's projections and exact read authority without
 * disturbing unrelated loaded history. The fetched page is rendered through
 * the query cache before its replacement read batch can be submitted.
 */
export function installRefreshedReadAuthority(
  current: InfiniteData<MessagePage>,
  staleMessageIds: string[],
  authorityPage: MessagePage,
  cursor: string | null,
): MessageHistoryData {
  if (cursor === null) {
    const merged = mergeHeadAuthority(current, authorityPage);
    const staleSignature = batchSignature(staleMessageIds);
    return {
      ...merged,
      readAuthorityBatches: combineReadAuthority(
        exactReadAuthority(merged).filter(
          (batch) => batchSignature(batch) !== staleSignature,
        ),
        authorityPage.read_message_ids.length > 0
          ? [authorityPage.read_message_ids]
          : [],
      ),
    };
  }
  const protectedPage = preserveTombstonesInPage(
    authorityPage,
    confirmedTombstones(current),
  );
  const staleSignature = batchSignature(staleMessageIds);
  const retained = retainStillEligibleAuthority(
    exactReadAuthority(current).filter(
      (batch) => batchSignature(batch) !== staleSignature,
    ),
    [protectedPage],
  );
  const authoritativeMessages = new Map(
    protectedPage.items.map((message) => [message.message_id, message]),
  );
  const overlaid = current.pages.map((page) => ({
    ...page,
    items: page.items.map(
      (message) => authoritativeMessages.get(message.message_id) ?? message,
    ),
  }));
  const targetIndex = current.pageParams.findIndex(
    (pageParam) => pageParam === cursor,
  );
  const overlapIndex = overlaid.findIndex((page) =>
    page.items.some((message) => authoritativeMessages.has(message.message_id)),
  );
  const insertionIndex =
    targetIndex >= 0
      ? targetIndex
      : overlapIndex >= 0
        ? overlapIndex
        : current.pages.length;
  const candidates = overlaid.map((page, index) => ({
    page,
    pageParam: current.pageParams[index],
  }));
  candidates.splice(insertionIndex, 0, {
    page: protectedPage,
    pageParam: cursor,
  });
  return rebuildMessageHistory(
    current,
    candidates,
    combineReadAuthority(
      retained,
      protectedPage.read_message_ids.length > 0
        ? [protectedPage.read_message_ids]
        : [],
    ),
  );
}
